import {Injectable} from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import {from, Observable, throwError} from 'rxjs';
import {catchError, mergeMap} from 'rxjs/operators';
import {
  IAnalyticsDevicesRequest,
  IAnalyticsDevicesReportRequest,
  IAnalyticsDevicesResponse,
  IOfflineDevicesSummary
} from '@interfaces/analytics';
import {createHttpParams} from '@shared/functions';
import {ErrorService} from '@services/error/error.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {
  }

  getOfflineDevicesSummary(): Observable<IOfflineDevicesSummary> {
    return this.http
      .get<IOfflineDevicesSummary>('analytics/offline-devices/summary')
      .pipe(catchError(error => this.handleError(error)));
  }

  getOfflineDevices(request: IAnalyticsDevicesRequest): Observable<IAnalyticsDevicesResponse> {
    let params = createHttpParams({
      skip: request.skip,
      limit: request.limit,
      search: request.search || '',
      sort: request.sort || 'lastDisconnectAt',
      order: request.order || 'DESC'
    });
    request.period?.forEach(period => {
      params = params.append('period', period);
    });

    return this.http
      .get<IAnalyticsDevicesResponse>('analytics/offline-devices', {params})
      .pipe(catchError(error => this.handleError(error)));
  }

  downloadOfflineDevicesReport(
    request: IAnalyticsDevicesReportRequest
  ): Observable<HttpResponse<Blob>> {
    let params = createHttpParams({
      search: request.search || '',
      sort: request.sort || 'lastDisconnectAt',
      order: request.order || 'DESC'
    });
    request.period?.forEach(period => {
      params = params.append('period', period);
    });

    return this.http
      .get('analytics/offline-devices/export', {
        params,
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(catchError(error => this.handleBlobError(error)));
  }

  private handleBlobError(error: HttpErrorResponse): Observable<never> {
    if (!(error.error instanceof Blob)) {
      return this.handleError(error);
    }

    return from(error.error.text()).pipe(
      mergeMap(errorText => {
        let parsedError: unknown = errorText;

        try {
          parsedError = JSON.parse(errorText);
        } catch {
          // Keep the response text when the server did not return JSON.
        }

        return this.handleError(new HttpErrorResponse({
          error: parsedError,
          headers: error.headers,
          status: error.status,
          statusText: error.statusText,
          url: error.url || undefined
        }));
      })
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    this.errorService.showError(error);
    return throwError(error);
  }
}
